// frontend/src/pages/Clients.js - Version avec modales
import React, { useEffect, useState } from 'react';
import { customersService, importExportService } from '../services/api';
import { usePermissions } from '../hooks/usePermissions';
import { PermissionGuard } from '../components/PermissionGuard';
import { PERMISSIONS } from '../utils/permissions';
import Pagination from '../components/Pagination';
import AlertModal from '../components/AlertModal';
import SMSModal from '../components/SMSModal';
import { Plus, Search, Edit, Trash2, Loader, X, Check, Download, Upload, AlertCircle, Eye, Ban, CheckCircle, MessageSquare } from 'lucide-react';

const Clients = () => {
  const { hasPermission } = usePermissions();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchField, setSearchField] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [showSMSModal, setShowSMSModal] = useState(false);
  const [smsMessage, setSmsMessage] = useState('');
  const [smsCustomer, setSmsCustomer] = useState(null);

  const [showBulkSMSModal, setShowBulkSMSModal] = useState(false);


  // États pour les modales d'alerte
  const [alertModal, setAlertModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'info',
    onConfirm: null,
    showCancel: false,
    confirmText: 'OK',
    cancelText: 'Annuler'
  });

  const itemsPerPage = 20;

  const [formData, setFormData] = useState({
    customer_id: '',
    full_name: '',
    phone: '',
    phone2: '',
    email: '',
    address: '',
    district: '',
    zone: '',
    neighborhood: '',
    id_card_type: '',
    id_card_number: ''
  });

  // Fonction helper pour afficher une alerte
  const showAlert = (config) => {
    setAlertModal({
      isOpen: true,
      ...config
    });
  };

  const closeAlert = () => {
    setAlertModal({
      isOpen: false,
      title: '',
      message: '',
      type: 'info',
      onConfirm: null,
      showCancel: false
    });
  };

  useEffect(() => {
    fetchCustomers();
  }, [currentPage, searchTerm, searchField]);

useEffect(() => {
  const timer = setTimeout(() => {
    setCurrentPage(1); // ❌ ne pas appeler fetch ici
  }, 400);

  return () => clearTimeout(timer);
}, [searchTerm, searchField]);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const skip = (currentPage - 1) * itemsPerPage;
      const response = await customersService.getAll({
        search: searchTerm,
        search_field: searchField,
        skip,
        limit: itemsPerPage
      });
      setCustomers(response.data.customers || response.data);
      setTotalCustomers(response.data.total || response.data.length);
      setTotalPages(Math.ceil((response.data.total || response.data.length) / itemsPerPage));
      setError('');
    } catch (error) {
      console.error('Erreur:', error);
      showAlert({
        title: 'Erreur',
        message: 'Erreur lors du chargement des clients',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (editingCustomer && !hasPermission(PERMISSIONS.EDIT_CUSTOMER)) {
      showAlert({
        title: 'Permission refusée',
        message: 'Vous n\'avez pas la permission de modifier les clients',
        type: 'error'
      });
      return;
    }
    if (!editingCustomer && !hasPermission(PERMISSIONS.ADD_CUSTOMER)) {
      showAlert({
        title: 'Permission refusée',
        message: 'Vous n\'avez pas la permission d\'ajouter des clients',
        type: 'error'
      });
      return;
    }

    setLoading(true);
    setError('');

    try {
      if (editingCustomer) {
        await customersService.update(editingCustomer.customer_id, formData);
        showAlert({
          title: 'Succès',
          message: 'Client modifié avec succès',
          type: 'success'
        });
      } else {
        await customersService.create(formData);
        showAlert({
          title: 'Succès',
          message: 'Client créé avec succès',
          type: 'success'
        });
      }
      setShowModal(false);
      resetForm();
      fetchCustomers();
    } catch (error) {
      setError(error.response?.data?.detail || 'Erreur lors de l\'opération');
      showAlert({
        title: 'Erreur',
        message: error.response?.data?.detail || 'Erreur lors de l\'opération',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (customer) => {
    if (!hasPermission(PERMISSIONS.EDIT_CUSTOMER)) {
      showAlert({
        title: 'Permission refusée',
        message: 'Vous n\'avez pas la permission de modifier les clients',
        type: 'error'
      });
      return;
    }

    setEditingCustomer(customer);
    setFormData({
      customer_id: customer.customer_id,
      full_name: customer.full_name,
      phone: customer.phone,
      phone2: customer.phone2 || '',
      email: customer.email || '',
      address: customer.address || '',
      district: customer.district || '',
      zone: customer.zone || '',
      neighborhood: customer.neighborhood || '',
      id_card_type: customer.id_card_type || '',
      id_card_number: customer.id_card_number || ''
    });
    setShowModal(true);
    setError('');
  };

  const handleViewDetails = async (customer) => {
    try {
      const response = await customersService.getById(customer.customer_id);
      setSelectedCustomer(response.data);
      setShowDetailsModal(true);
    } catch (error) {
      showAlert({
        title: 'Erreur',
        message: 'Erreur lors du chargement des détails',
        type: 'error'
      });
    }
  };

  // ✅ Nouvelle fonction de suppression intelligente
  const handleDelete = async (customer) => {
    if (!hasPermission(PERMISSIONS.DELETE_CUSTOMER)) {
      showAlert({
        title: 'Permission refusée',
        message: 'Vous n\'avez pas la permission de supprimer les clients',
        type: 'error'
      });
      return;
    }

    showAlert({
      title: 'Confirmation de suppression',
      message: `Êtes-vous sûr de vouloir supprimer le client "${customer.full_name}" ?\n\n⚠️ Cette action est irréversible.`,
      type: 'confirm',
      showCancel: true,
      confirmText: 'Supprimer',
      cancelText: 'Annuler',
      onConfirm: () => confirmDelete(customer)
    });

  };
  const confirmDelete = async (customer) => {
    try {
      // tentative normale (client sans compteur)
      await customersService.delete(customer.customer_id, false);

      showAlert({
        title: 'Suppression réussie',
        message: `Le client "${customer.full_name}" a été supprimé avec succès.`,
        type: 'success'
      });

      fetchCustomers();
    } catch (error) {
      const errorType = error.response?.headers?.['x-error-type'];
      const errorDetail = error.response?.data?.detail || 'Erreur inconnue';

      // 🔴 Compteurs actifs
      if (errorType === 'active_meters') {
        showAlert({
          title: '❌ Suppression impossible',
          message: `${errorDetail}\n\n💡 Désactivez d'abord tous les compteurs actifs.`,
          type: 'error'
        });
      }

      // 🟠 Compteurs inactifs → confirmation spéciale
      else if (errorType === 'inactive_meters') {
        const meterCount = error.response?.headers?.['x-meter-count'];

        showAlert({
          title: '⚠️ Confirmation requise',
          message: `Le client "${customer.full_name}" possède ${meterCount} compteur(s) inactif(s).\n\nVoulez-vous supprimer le client ET ses compteurs ?`,
          type: 'confirm',
          showCancel: true,
          confirmText: 'Supprimer tout',
          cancelText: 'Annuler',
          onConfirm: () => handleDeleteWithMeters(customer)
        });
      }

      // 🔴 Transactions récentes
      else if (errorType === 'recent_transactions') {
        showAlert({
          title: '❌ Suppression impossible',
          message: `${errorDetail}\n\n💡 Attendez 30 jours ou désactivez le client.`,
          type: 'error'
        });
      }

      // ⚠️ Autre erreur
      else {
        showAlert({
          title: 'Erreur',
          message: errorDetail,
          type: 'error'
        });
      }
    }
  };

  // Suppression forcée avec compteurs
  const handleDeleteWithMeters = async (customer) => {
    try {
      await customersService.delete(customer.customer_id, true);
      showAlert({
        title: 'Suppression réussie',
        message: `Le client "${customer.full_name}" et ses compteurs ont été supprimés.`,
        type: 'success'
      });
      fetchCustomers();
    } catch (error) {
      showAlert({
        title: 'Erreur',
        message: error.response?.data?.detail || 'Erreur lors de la suppression',
        type: 'error'
      });
    }
  };

  const handleBlock = async (customer) => {
    if (!hasPermission(PERMISSIONS.BLOCK_CUSTOMER)) {
      showAlert({
        title: 'Permission refusée',
        message: 'Vous n\'avez pas la permission de bloquer/débloquer les clients',
        type: 'error'
      });
      return;
    }

    const action = customer.is_active ? 'bloquer' : 'débloquer';

    showAlert({
      title: `${action.charAt(0).toUpperCase() + action.slice(1)} le client`,
      message: `Êtes-vous sûr de vouloir ${action} le client "${customer.full_name}" ?`,
      type: 'confirm',
      showCancel: true,
      confirmText: action.charAt(0).toUpperCase() + action.slice(1),
      onConfirm: async () => {
        try {
          await customersService.toggleBlock(customer.customer_id);
          showAlert({
            title: 'Succès',
            message: `Client ${action === 'bloquer' ? 'bloqué' : 'débloqué'} avec succès`,
            type: 'success'
          });
          fetchCustomers();
        } catch (error) {
          showAlert({
            title: 'Erreur',
            message: `Erreur lors du ${action}age`,
            type: 'error'
          });
        }
      }
    });
  };

  const handleSendSMS = (customer) => {
    setSmsCustomer(customer);
    setSmsMessage(`Bonjour ${customer.full_name}, `);
    setShowSMSModal(true);
  };
  const sendIndividualSMS = async (message) => {
    try {
      const response = await customersService.sendSMS(smsCustomer.customer_id, message);
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // ✅ SMS Groupé
  const handleBulkSMS = () => {
    setShowBulkSMSModal(true);
  };
  const sendBulkSMS = async (message) => {
    // Récupérer les clients filtrés
    const customerIds = customers
      .filter(c => c.is_active) // Seulement les actifs
      .map(c => c.customer_id);

    try {
      const response = await customersService.sendBulkSMS({
        customer_ids: customerIds,
        message: message
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const sendSMS = async () => {
    if (!smsMessage.trim()) {
      showAlert({
        title: 'Attention',
        message: 'Le message est vide',
        type: 'warning'
      });
      return;
    }

    try {
      await customersService.sendSMS(smsCustomer.customer_id, smsMessage);
      showAlert({
        title: 'Succès',
        message: 'SMS envoyé avec succès',
        type: 'success'
      });
      setShowSMSModal(false);
      setSmsMessage('');
      setSmsCustomer(null);
    } catch (error) {
      showAlert({
        title: 'Erreur',
        message: 'Erreur lors de l\'envoi du SMS',
        type: 'error'
      });
    }
  };

  const handleExport = async () => {
    if (!hasPermission(PERMISSIONS.EXPORT_REPORTS)) {
      showAlert({
        title: 'Permission refusée',
        message: 'Vous n\'avez pas la permission d\'exporter',
        type: 'error'
      });
      return;
    }
    try {
      const response = await importExportService.exportClients();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `clients_${new Date().getTime()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      showAlert({
        title: 'Succès',
        message: 'Export effectué avec succès',
        type: 'success'
      });
    } catch (error) {
      showAlert({
        title: 'Erreur',
        message: 'Erreur lors de l\'export',
        type: 'error'
      });
    }
  };

  const handleImport = async (event) => {
    if (!hasPermission(PERMISSIONS.ADD_CUSTOMER)) {
      showAlert({
        title: 'Permission refusée',
        message: 'Vous n\'avez pas la permission d\'importer des clients',
        type: 'error'
      });
      return;
    }
    const file = event.target.files[0];
    if (!file) return;

    try {
      const result = await importExportService.importClients(file);
      const errors = result.data.errors || [];
      const message = errors.length > 0
        ? `${result.data.created} clients importés\n\n⚠️ Erreurs :\n${errors.join('\n')}`
        : `${result.data.created} clients importés avec succès`;

      showAlert({
        title: errors.length > 0 ? 'Import partiel' : 'Succès',
        message: message,
        type: errors.length > 0 ? 'warning' : 'success'
      });
      fetchCustomers();
    } catch (error) {
      showAlert({
        title: 'Erreur',
        message: 'Erreur lors de l\'import',
        type: 'error'
      });
    }
    event.target.value = '';
  };

  const resetForm = () => {
    setEditingCustomer(null);
    setFormData({
      customer_id: '',
      full_name: '',
      phone: '',
      phone2: '',
      email: '',
      address: '',
      district: '',
      zone: '',
      neighborhood: '',
      id_card_type: '',
      id_card_number: ''
    });
    setError('');
  };

  const getRiskScoreColor = (score) => {
    const colors = {
      'faible': 'bg-green-100 text-green-800',
      'moyen': 'bg-yellow-100 text-yellow-800',
      'élevé': 'bg-red-100 text-red-800',
      'actif': 'bg-blue-100 text-blue-800'
    };
    return colors[score?.toLowerCase()] || 'bg-gray-100 text-gray-800';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XOF' }).format(amount || 0);
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('fr-FR');
  };

  return (
    <div className="space-y-6">

      {/* Modale d'alerte globale */}
      <AlertModal
        isOpen={alertModal.isOpen}
        onClose={closeAlert}
        title={alertModal.title}
        message={alertModal.message}
        type={alertModal.type}
        onConfirm={alertModal.onConfirm}
        showCancel={alertModal.showCancel}
        confirmText={alertModal.confirmText}
        cancelText={alertModal.cancelText}
      />
      {/* Modales SMS */}
      <SMSModal
        isOpen={showSMSModal}
        onClose={() => {
          setShowSMSModal(false);
          setSmsCustomer(null);
        }}
        onSend={sendIndividualSMS}
        customer={smsCustomer}
      />

      <SMSModal
        isOpen={showBulkSMSModal}
        onClose={() => setShowBulkSMSModal(false)}
        onSend={sendBulkSMS}
        bulkMode={true}
        bulkCount={customers.filter(c => c.is_active).length}
        customer={customers[0]} // Pour preview
      />

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestion des Clients</h1>
          <p className="text-gray-600 text-sm mt-1">
            {totalCustomers} client{totalCustomers > 1 ? 's' : ''} enregistré{totalCustomers > 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {/* Bouton SMS Groupé */}
          <button
            onClick={handleBulkSMS}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
            disabled={customers.length === 0}
          >
            <MessageSquare size={20} />
            SMS Groupé ({customers.filter(c => c.is_active).length})
          </button>
          <PermissionGuard permission={PERMISSIONS.EXPORT_REPORTS}>
            <button
              onClick={handleExport}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
            >
              <Download size={20} />
              Exporter
            </button>
          </PermissionGuard>
          <PermissionGuard permission={PERMISSIONS.ADD_CUSTOMER}>
            <label className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors flex items-center gap-2 cursor-pointer">
              <Upload size={20} />
              Importer
                <input type="file" accept=".xlsx,.xls" onChange={handleImport} className="hidden" />
            </label>
          </PermissionGuard>
          <PermissionGuard permission={PERMISSIONS.ADD_CUSTOMER}>
            <button
              onClick={() => { resetForm(); setShowModal(true); }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 shadow-sm"
            >
              <Plus size={20} />
              Nouveau Client
            </button>
          </PermissionGuard>
        </div>
      </div>
      {/* Barre de recherche */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Rechercher..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <select
            value={searchField}
            onChange={(e) => setSearchField(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">Tous les champs</option>
            <option value="name">Nom</option>
            <option value="phone">Téléphone</option>
            <option value="email">Email</option>
            <option value="address">Adresse</option>
            <option value="district">District</option>
            <option value="zone">Zone</option>
          </select>
        </div>
      </div>

      {/* Table des clients */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : customers.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <p>Aucun client trouvé</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">ID</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Nom</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Téléphone</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Village/District</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Compteurs</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Total Achats</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Statut</th>
                    <PermissionGuard permissions={[PERMISSIONS.EDIT_CUSTOMER, PERMISSIONS.DELETE_CUSTOMER, PERMISSIONS.BLOCK_CUSTOMER]}>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Actions</th>
                    </PermissionGuard>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {customers.map((customer) => (
                    <tr key={customer.id} className="hover:bg-gray-50 transition-colors">
                      <td className="py-3 px-4 font-mono text-sm font-semibold text-gray-900">{customer.customer_id}</td>
                      <td className="py-3 px-4 text-sm text-gray-900">{customer.full_name}</td>
                      <td className="py-3 px-4 text-sm text-gray-700">{customer.phone}</td>
                      <td className="py-3 px-4 text-sm text-gray-700">{customer.address}</td>
                      <td className="py-3 px-4 text-sm">
                        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium">
                          {customer.meter_count || 0}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm font-semibold text-gray-900">
                        {formatCurrency(customer.total_purchases || 0)}
                      </td>
                      <td className="py-3 px-4">
                        {customer.is_active ? (
                          <span className="flex items-center gap-1 text-green-600 text-sm">
                            <CheckCircle size={16} />
                            Actif
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-red-600 text-sm">
                            <Ban size={16} />
                            Bloqué
                          </span>
                        )}
                      </td>
                      <PermissionGuard permissions={[PERMISSIONS.EDIT_CUSTOMER, PERMISSIONS.DELETE_CUSTOMER, PERMISSIONS.BLOCK_CUSTOMER]}>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleViewDetails(customer)}
                              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                              title="Voir détails"
                            >
                              <Eye size={18} />
                            </button>
                            <PermissionGuard permission={PERMISSIONS.EDIT_CUSTOMER}>
                              <button
                                onClick={() => handleEdit(customer)}
                                className="p-1.5 text-green-600 hover:bg-green-50 rounded transition-colors"
                                title="Modifier"
                              >
                                <Edit size={18} />
                              </button>
                            </PermissionGuard>
                            <button
                              onClick={() => handleSendSMS(customer)}
                              className="p-1.5 text-purple-600 hover:bg-purple-50 rounded transition-colors"
                              title="Envoyer SMS"
                              disabled={!customer.phone}
                            >
                              <MessageSquare size={18} />
                            </button>

                            <PermissionGuard permission={PERMISSIONS.BLOCK_CUSTOMER}>
                              <button
                                onClick={() => handleBlock(customer)}
                                className={`p-1.5 rounded transition-colors ${customer.is_active
                                    ? 'text-orange-600 hover:bg-orange-50'
                                    : 'text-green-600 hover:bg-green-50'
                                  }`}
                                title={customer.is_active ? 'Bloquer' : 'Débloquer'}
                              >
                                {customer.is_active ? <Ban size={18} /> : <CheckCircle size={18} />}
                              </button>
                            </PermissionGuard>
                            <PermissionGuard permission={PERMISSIONS.DELETE_CUSTOMER}>
                              <button
                                onClick={() => handleDelete(customer)}
                                className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                                title="Supprimer"
                              >
                                <Trash2 size={18} />
                              </button>
                            </PermissionGuard>
                          </div>
                        </td>
                      </PermissionGuard>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              totalItems={totalCustomers}
              itemsPerPage={itemsPerPage}
            />
          </>
        )}
      </div>

      {/* Modal de création/édition */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white">
              <h2 className="text-xl font-bold text-gray-900">
                {editingCustomer ? 'Modifier le Client' : 'Nouveau Client'}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                  <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={18} />
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    ID Client
                    {editingCustomer ? (
                      <span className="text-xs text-red-500 ml-2">(non modifiable)</span>
                    ) : (
                      <span className="text-xs text-green-600 ml-2">(✓ généré automatiquement)</span>
                    )}
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={formData.customer_id}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                      disabled
                      placeholder={editingCustomer ? formData.customer_id : "Auto-généré (ex: CLT-0001)"}
                      readOnly
                    />
                    {!editingCustomer && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 text-green-600">
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                  </div>
                  {!editingCustomer && (
                    <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                      L'ID sera automatiquement généré lors de la création (ex: CLT-0001, CLT-0002, etc.)
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nom Complet *</label>
                  <input
                    type="text"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                    placeholder="Ex: Amadou Diop"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Téléphone Principal *</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="+221xxxxxxxxx"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Téléphone Secondaire</label>
                  <input
                    type="tel"
                    value={formData.phone2}
                    onChange={(e) => setFormData({ ...formData, phone2: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="+221xxxxxxxxx"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="exemple@email.com"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Adresse</label>
                  <textarea
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    rows="2"
                    placeholder="Adresse complète"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Departement</label>
                  <input
                    type="text"
                    value={formData.district}
                    onChange={(e) => setFormData({ ...formData, district: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Ex: Dakar"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Commune</label>
                  <input
                    type="text"
                    value={formData.zone}
                    onChange={(e) => setFormData({ ...formData, zone: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Ex: Zone A"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Village/Quartier</label>
                  <input
                    type="text"
                    value={formData.neighborhood}
                    onChange={(e) => setFormData({ ...formData, neighborhood: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Ex: Plateau"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Type de Pièce d'Identité</label>
                  <select
                    value={formData.id_card_type}
                    onChange={(e) => setFormData({ ...formData, id_card_type: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    
                  >
                    <option value="">Sélectionner...</option>
                    <option value="CNI">CNI</option>
                    <option value="Passport">Passeport</option>
                    <option value="Permis">Permis de conduire</option>
                    <option value="Autre">Autre</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Numéro de Pièce</label>
                  <input
                    type="text"
                    value={formData.id_card_number}
                    onChange={(e) => setFormData({ ...formData, id_card_number: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Numéro d'identification"
                    required
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  disabled={loading}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {loading ? <Loader className="w-5 h-5 animate-spin" /> : <Check size={20} />}
                  {editingCustomer ? 'Mettre à jour' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal détails */}
      {showDetailsModal && selectedCustomer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white">
              <h2 className="text-xl font-bold text-gray-900">Détails du Client</h2>
              <button onClick={() => setShowDetailsModal(false)} className="text-gray-400 hover:text-gray-600">
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Informations Personnelles</h3>
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs text-gray-500">ID Client</p>
                      <p className="font-mono font-semibold">{selectedCustomer.customer_id}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Nom Complet</p>
                      <p className="font-medium">{selectedCustomer.full_name}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Téléphone Principal</p>
                      <p>{selectedCustomer.phone}</p>
                    </div>
                    {selectedCustomer.phone2 && (
                      <div>
                        <p className="text-xs text-gray-500">Téléphone Secondaire</p>
                        <p>{selectedCustomer.phone2}</p>
                      </div>
                    )}
                    {selectedCustomer.email && (
                      <div>
                        <p className="text-xs text-gray-500">Email</p>
                        <p>{selectedCustomer.email}</p>
                      </div>
                    )}
                    {selectedCustomer.address && (
                      <div>
                        <p className="text-xs text-gray-500">Adresse</p>
                        <p>{selectedCustomer.address}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-xs text-gray-500">Statut</p>
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${selectedCustomer.is_active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                        }`}>
                        {selectedCustomer.is_active ? 'Actif' : 'Bloqué'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Statistiques */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">
                    Statistiques & Activité
                  </h3>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-xs text-gray-500">Compteurs associés</p>
                      <p className="text-2xl font-bold text-blue-600">
                        {selectedCustomer.meter_count || 0}
                      </p>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-xs text-gray-500">Score de risque</p>
                      <span className={`inline-block mt-1 px-3 py-1 rounded-full text-sm font-medium ${getRiskScoreColor(selectedCustomer.risk_score)}`}>
                        {selectedCustomer.risk_score || 'Actif'}
                      </span>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-4 col-span-2">
                      <p className="text-xs text-gray-500">Total des achats</p>
                      <p className="text-xl font-bold text-gray-900">
                        {formatCurrency(selectedCustomer.total_purchases)}
                      </p>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-4 col-span-2">
                      <p className="text-xs text-gray-500">Dernier achat</p>
                      {selectedCustomer.last_purchase ? (
                        <div>
                          <p className="font-semibold">
                            {formatCurrency(selectedCustomer.last_purchase.amount)}
                          </p>
                          <p className="text-xs text-gray-500">
                            {formatDate(selectedCustomer.last_purchase.date)}
                          </p>
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">Aucun achat</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-3 pt-4 border-t">
                <button
                  onClick={() => {
                    setShowDetailsModal(false);
                    handleSendSMS(selectedCustomer);
                  }}
                  className="px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center justify-center gap-2"
                >
                  <MessageSquare size={18} />
                  Envoyer SMS
                </button>


                <button
                  onClick={() => handleBlock(selectedCustomer)}
                  className={`px-4 py-2 rounded-lg flex items-center gap-2 ${selectedCustomer.is_active
                      ? 'bg-orange-600 hover:bg-orange-700 text-white'
                      : 'bg-green-600 hover:bg-green-700 text-white'
                    }`}
                >
                  {selectedCustomer.is_active ? <Ban size={18} /> : <CheckCircle size={18} />}
                  {selectedCustomer.is_active ? 'Bloquer' : 'Débloquer'}
                </button>

                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="ml-auto px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Clients;
